import quickSearchQuery from '~layers/app/graphql/quickSearch.gql'

export function useQuickSearch() {
  const { profileKey, sessionKey, lastRequestId, track: trackTweakwise } = useTweakwise()
  const search = useState('search', () => '')
  const searchDialogOpen = useState('searchDialogOpen', () => false)

  // Request
  const recentSearches = useLocalStorage<string[]>('recentSearches', [], { initOnMounted: true })

  const variables = computed(() => ({
    profileKey: profileKey.value,
    search: search.value
      .replace(/\.\B/g, '') // remove trailing dot
      // eslint-disable-next-line
      .replace(/[\\\/]/g, ' ')
      .replace(/  +/g, ' ') // remove double (or more) spaces
      .trim(), // remove slashes
  }))
  const enabled = computed(() => search.value.length > 2)
  const { result: data, loading, error, onResult } = useQuery(quickSearchQuery, variables, {
    enabled,
    debounce: 500,
  })

  onResult((result) => {
    if (result.loading) {
      return
    }
    const searchTerm = result?.data?.twSearch?.term
    const newRecentSearches = new Set([searchTerm, ...recentSearches.value])
    recentSearches.value = Array.from(newRecentSearches).slice(0, 5)
    lastRequestId.value = result?.data?.twSearch?.requestId || null
    trackTweakwise('search', {
      SearchTerm: searchTerm,
      SessionKey: sessionKey.value,
      ProfileKey: profileKey.value,
    })
  })

  // Tweakwise does not guarantee the order or number of item blocks, so classify
  // every item by its type instead of relying on the block position.
  const rawItemBlocks = computed<any[]>(() => data.value?.twSearch?.itemBlocks || [])

  const isProduct = (item: any) => item?.__typename === 'EsProduct'
  const isPage = (item: any) => item?.type === 'page'

  const productBlocks = computed(() =>
    rawItemBlocks.value.filter((block: any) => block?.items?.some(isProduct)),
  )

  const products = computed(() => {
    const seen = new Set()
    return productBlocks.value
      .flatMap((block: any) => block.items.filter(isProduct))
      .filter((product: any) => {
        if (seen.has(product.id)) {
          return false
        }
        seen.add(product.id)
        return true
      })
  })

  const totalProducts = computed(() =>
    productBlocks.value.reduce((acc: number, block: any) => acc + (block.nrOfItems || 0), 0),
  )

  // Group page items by their (Tweakwise translated) block name
  const itemBlocks = computed(() => {
    const groups = new Map<string, any[]>()
    for (const block of rawItemBlocks.value) {
      const pages = (block?.items || []).filter(isPage)
      if (!pages.length) {
        continue
      }
      const name = block.name || ''
      groups.set(name, [...(groups.get(name) || []), ...pages])
    }
    return Array.from(groups, ([name, items]) => ({ name, items, nrOfItems: items.length }))
  })
  const suggestionBlocks = computed(() => data.value?.twSearch?.suggestionBlocks || [])

  const redirects = computed(() => data.value?.twSearch?.redirects || [])

  const searchRedirect = () => {
    if (!search.value) {
      return
    }
    const router = useRouter()

    if (redirects.value.length) {
      return router.push(redirects.value[0].url)
    }

    router.push({ path: '/search/', query: { q: search.value } })
  }

  return {
    data,
    search,
    searchDialogOpen,
    recentSearches,
    products,
    totalProducts,
    itemBlocks,
    suggestionBlocks,
    error,
    loading,
    searchRedirect,
    redirects,
  }
}
